import { useState, useEffect, useRef } from 'react';
import styles from './NovaChat.module.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function NovaChat({
  apiUrl,
  apiKey,
  domain,
  companyId,
  userName,
  userId
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [userData, setUserData] = useState({
    consulta: ''
  });
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatId, setChatId] = useState(null);
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [error, setError] = useState(null);
  const chatBodyRef = useRef(null);
  const messageIdCounterRef = useRef(0);
  const [isResponding, setIsResponding] = useState(false);

  const [npsQuestion, setNpsQuestion] = useState(null);
  const [npsQuestionId, setNpsQuestionId] = useState(null);
  const [showNpsSurvey, setShowNpsSurvey] = useState(false);
  const [selectedNpsScore, setSelectedNpsScore] = useState(null);
  const [isSubmittingNps, setIsSubmittingNps] = useState(false);
  const [npsSubmitted, setNpsSubmitted] = useState(false);
  const [npsError, setNpsError] = useState(null);
  const [sourcesPanel, setSourcesPanel] = useState({
    isOpen: false,
    sources: [],
  });
  const [isChatBlocked, setIsChatBlocked] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const resetChat = () => {
    setShowForm(true);
    setUserData({ consulta: '' });
    setMessages([]);
    setInputMessage('');
    setChatId(null);
    setError(null);
    setNpsSubmitted(false);
    setShowNpsSurvey(false);
    setSelectedNpsScore(null);
    setNpsError(null);
    setSourcesPanel({ isOpen: false, sources: [] });
    setIsChatBlocked(false);
    setIsResponding(false);
    messageIdCounterRef.current = 0;
  };

  const handleRestartChat = () => {
    setIsResetting(true);
    toggleWidget();
  };

  const getNextMessageId = () => {
    messageIdCounterRef.current += 1;
    return messageIdCounterRef.current;
  };

  const handleNpsScoreSelect = async (score) => {
    if (isSubmittingNps || !npsQuestionId || !chatId) {
      console.warn("NPS submit prevented:", {isSubmittingNps, npsQuestionId, chatId});
      return;
    }

    setSelectedNpsScore(score);
    setIsSubmittingNps(true);
    setNpsError(null);


    try {
      const response = await fetch(`${apiUrl}/nps/saveNPSAnswer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Origin': domain || window.location.origin
        },
        body: JSON.stringify({
          channel_name: 'widget-chat',
          company_id: companyId,
          name: userName || 'Usuario Chat',
          client_id: userId || chatId || 'unknown',
          question_id: npsQuestionId,
          answer: score.toString()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Error ${response.status} al guardar NPS`);
      }

      setNpsSubmitted(true);
      setShowNpsSurvey(false);

    } catch (err) {
      console.error("Error submitting NPS answer:", err);
      setNpsError(err.message || "No se pudo guardar tu respuesta. Intenta de nuevo.");
    } finally {
      setIsSubmittingNps(false);
    }
  };

  const fixCorruptedEncoding = (str) => {
    if (!str) return '';
    let processedStr = str;
    try {
      processedStr = decodeURIComponent(escape(str));
    } catch (e) {
      console.warn("Could not fix encoding for string:", str, e);
    }
    return processedStr.replace(/_/g, ' ');
  };

  const handleShowSources = (sources) => {
    setSourcesPanel({ isOpen: true, sources });
  };

  const handleCloseSources = () => {
    setSourcesPanel({ isOpen: false, sources: [] });
  };

  const toggleWidget = () => {
    const closing = isOpen;
    setIsOpen(!isOpen);

    if (closing && npsQuestion && !npsSubmitted && chatId !== null) {
      setShowNpsSurvey(true);
      setSelectedNpsScore(null);
      setNpsError(null);
    } else {
      setShowNpsSurvey(false);
    }
  };

  
  const startChat = async (initialMessage) => {
    setIsStartingChat(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/chats-company/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Origin': domain || window.location.origin
        },
        body: JSON.stringify({ initialMessage })
      });

      if (!response.ok) {
        let errorMessage = `Error ${response.status}: No se pudo iniciar el chat.`;
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            console.error("Error response (text) from /chats-company/start:", errorText);
          }
        } catch (e) {
          console.error("Could not parse error response from /chats-company/start:", e);
        }
        console.error("Full error details:", { status: response.status, statusText: response.statusText });
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.chat_id) {
         console.error("No chat_id received from /chats-company/start");
         throw new Error('No se recibió un ID de chat válido.');
      }

      setChatId(data.chat_id);

      
      const userInitialMessageId = getNextMessageId();
      const assistantWelcomeMessageId = getNextMessageId();

      setMessages([
        {
          role: 'user',
          content: initialMessage || 'Hola, me gustaría iniciar una conversación.',
          messageId: userInitialMessageId
        },
        {
          role: 'assistant',
          content: `Hola ${userName}, en breve respondo a tu consulta.`,
          messageId: assistantWelcomeMessageId,
          loading: false
        }
      ]);

      setShowForm(false);
      await sendMessage(initialMessage, data.chat_id);

    } catch (err) {
      console.error('Error starting chat:', err);
      setError(err.message || 'Ocurrió un error al iniciar el chat.');
      
    } finally {
      setIsStartingChat(false);
    }
  };

  
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const initialQuery = userData.consulta.trim();
    if (!initialQuery) {
      setError("Por favor, ingresa tu consulta inicial.");
      return;
    }
    
    startChat(initialQuery); 
  };

  const handleInputChange = (e) => {
    setUserData({ consulta: e.target.value });
  };

  const handleChatInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  
  const handleSendMessage = () => {
    const messageToSend = inputMessage.trim();
    if (messageToSend === '' || !chatId || isResponding) {
      if(!chatId) console.error("Cannot send message, chatId is null");
      return;
    }

    
    const newUserMessage = {
      role: 'user',
      content: messageToSend,
      messageId: getNextMessageId()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setInputMessage('');

    
    sendMessage(messageToSend, chatId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  
  const sendMessage = async (message, currentChatId) => {
    setError(null); 
    setIsResponding(true);

    const loadingMessageId = getNextMessageId();
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      loading: true,
      messageId: loadingMessageId,
      pdfSources: []
    }]);

    try {
      const response = await fetch(`${apiUrl}/chats-company/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Origin': domain || window.location.origin
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: message }], 
          chat_id: currentChatId
        })
      });

      if (!response.ok) {
         let errorMsg = `Error ${response.status}`;
         try {
           const contentType = response.headers.get("content-type");
           if (contentType && contentType.includes("application/json")) {
             const errorData = await response.json();
             errorMsg = errorData.message || errorMsg;
           } else {
             const errorText = await response.text();
             console.error(`Error response (text) from /chats-company/process (${response.status}):`, errorText);
           }
         } catch (e) {
           console.error("Could not parse error response from /chats-company/process:", e);
         }
         console.error("Full error details:", { status: response.status, statusText: response.statusText });
         throw new Error(errorMsg);
      }

      if (!response.body) {
        throw new Error('No response body available');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedError = null; 

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
           setMessages(prev => {
              const newMessages = prev.map(msg =>
                 msg.messageId === loadingMessageId ? { ...msg, loading: false } : msg
              );
              
              const userMessagesCount = newMessages.filter(m => m.role === 'user').length;
              const MAX_MESSAGES = 10; 

              if (userMessagesCount >= MAX_MESSAGES) {
                const errorMessage = `En cada conversación se permite un máximo de 10 consultas, por favor haz clic en el botón "Nueva conversación".`;
                setError(errorMessage);
                setIsChatBlocked(true);
              }
              return newMessages;
           });
           setIsResponding(false);
           break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
           const event = buffer.substring(0, boundary);
           buffer = buffer.substring(boundary + 2);

           if (event.startsWith('data:')) {
              const jsonStr = event.slice(5).trim();
              if (jsonStr === '[DONE]') { 
                 continue; 
              }

              try {
                 const parsed = JSON.parse(jsonStr);

                 if (parsed.error) {
                   console.error("Error received in stream:", parsed.error);
                   receivedError = parsed.error.message || 'Error desconocido en el stream.';

                   if (receivedError === "En cada conversación se permite un máximo de 10 consultas, por favor haz clic en el botón \"Nueva conversación\".") {
                    setIsChatBlocked(true);
                    setError(receivedError);
                   }
                   
                   setMessages(prev => prev.map(msg => 
                     msg.messageId === loadingMessageId 
                     ? { ...msg, content: `${receivedError}`, loading: false, isError: true } 
                     : msg
                   ));
                   reader.cancel();
                   break;
                 }

                 if (parsed.message) {
                    setMessages(prev => prev.map(msg => {
                      if (msg.messageId !== loadingMessageId) {
                        return msg;
                      }
                      
                      const updatedMsg = { ...msg };
              
                      if (parsed.message.content) {
                        updatedMsg.content += parsed.message.content;
                      }
              
                      if (parsed.message.pdfSources && parsed.message.pdfSources.length > 0) {
                        updatedMsg.pdfSources = parsed.message.pdfSources;
                      }
                      
                      return updatedMsg;
                    }));
                 }
              } catch (e) {
                 console.error('Error parsing JSON from stream:', e, 'Raw data:', jsonStr);
              }
           }
           
           boundary = buffer.indexOf('\n\n'); 
        } 
         if(receivedError) break;
      } 

       if (receivedError) {
          setError(receivedError); 
       }

    } catch (error) {
      console.error('Error sending/receiving message:', error);
      const messageContentForBubble = error?.message || 'Ocurrió un error inesperado.';
      if (messageContentForBubble === "En cada conversación se permite un máximo de 10 consultas, por favor haz clic en el botón \"Nueva conversación\".") {
        setIsChatBlocked(true);
      }
      setError(messageContentForBubble); 
      
      setMessages(prev => prev.map(msg => 
         msg.messageId === loadingMessageId 
         ? { ...msg, content: messageContentForBubble, loading: false, isError: true }
         : msg
      ));
      setIsResponding(false);
    }
  };

  
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  
  useEffect(() => {
    const fetchNpsQuestion = async () => {
      if (!companyId) {
        console.warn("NPS: companyId is required but not provided.");
        return;
      }
      try {
        const response = await fetch(`${apiUrl}/nps/getQuestionByChannelAndCompany?channel_name=widget-chat&company_id=${companyId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Origin': domain || window.location.origin
          }
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Error fetching NPS question');
        }
        if (data && data.question && data.id) {
          setNpsQuestion(data.question);
          setNpsQuestionId(data.id.toString());
        } else {
          console.warn("NPS question data received is invalid:", data);
        }
      } catch (err) {
        console.error("Error fetching NPS question:", err);
        setNpsError("No se pudo cargar la pregunta de satisfacción.");
      }
    };

    fetchNpsQuestion();
    
  }, [apiUrl, apiKey, companyId, domain]);

  useEffect(() => {
    if (isResetting && !showNpsSurvey) {
      resetChat();
      setIsResetting(false);
    }
  }, [isResetting, showNpsSurvey]);

  return (
    <> {}

      {}
      {!isOpen ? (
        
        !showNpsSurvey && (
            <div className={styles.widgetClosed} onClick={toggleWidget}>
               {}
            </div>
        )
      ) : (
        
        <div className={styles.widgetOpen}>
          <div className={styles.chatHeader}>
            <img src="/LOGO-INLEARNING.webp" alt="Inlearning Logo" className={styles.chatLogo} />
            <button className={styles.closeBtn} onClick={toggleWidget}>&times;</button>
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}

          {showForm ? (
            <div className={styles.formContainer}>
              <form id="user-form" onSubmit={handleFormSubmit}>
                <div className={styles.formGroup}>
                  <label htmlFor="consulta">Ingresa tu consulta para obtener una respuesta:</label>
                  <input type="text" id="consulta" required value={userData.consulta} onChange={handleInputChange} placeholder="Ejemplo: ¿Qué es la fotosíntesis?" disabled={isStartingChat} maxLength="200"/>
                  <div className={styles.charCounter}>{userData.consulta.length} / 200</div>
                </div>
                <button type="submit" disabled={isStartingChat}>
                  {isStartingChat ? 'Iniciando...' : 'Responder'}
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className={styles.chatBody} ref={chatBodyRef}>
                {messages.map((message) => {
                  const isLimitError = message.isError && message.content === "En cada conversación se permite un máximo de 10 consultas, por favor haz clic en el botón \"Nueva conversación\".";
                  if (isLimitError) {
                    return null;
                  }
                  
                  return (
                    <div key={message.messageId} className={`${message.role === 'user' ? styles.userMessage : message.role === 'assistant' ? styles.assistantMessage : styles.systemMessage} ${message.isError ? styles.errorMessageText : ''}`}>
                      {message.loading ? (<div className={styles.loadingDots}><span></span><span></span><span></span></div>) : (
                        <div className={styles.markdownContent}>
                          {message.content.split('\\n\\n').map((block, index) => (
                            <ReactMarkdown key={index} remarkPlugins={[remarkGfm]}>{block}</ReactMarkdown>
                          ))}
                        </div>
                      )}
                      {message.role === 'assistant' && !message.loading && message.pdfSources && message.pdfSources.length > 0 && (
                        <div className={styles.sourcesContainer}>
                          <button onClick={() => handleShowSources(message.pdfSources)} className={styles.viewSourcesButton}>
                            Ver fuentes
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {isChatBlocked ? (
                <div className={styles.blockedFooter}>
                  <button onClick={handleRestartChat} className={styles.restartChatButtonLarge}>
                    Nueva conversación
                  </button>
                </div>
              ) : (
                <div className={styles.chatFooter}>
                  <input type="text" id="chat-input" placeholder="Ingresa tu nueva consulta" value={inputMessage} onChange={handleChatInputChange} onKeyPress={handleKeyPress} disabled={!chatId || isResponding} maxLength="200"/>
                  <button onClick={handleSendMessage} disabled={!chatId || inputMessage.trim() === '' || isResponding}>Responder</button>
                </div>
              )}
            </>
          )}
        </div>
      )} {}

      {}
      {sourcesPanel.isOpen && (
        <div className={styles.sourcesPanel}>
            <div className={styles.sourcesHeader}>
                <strong>Fuentes Consultadas</strong>
                <button onClick={handleCloseSources} className={styles.closeSourcesBtn}>&times;</button>
            </div>
            <ul className={styles.sourcesList}>
                {sourcesPanel.sources.map((source, index) => (
                    <li key={index}>
                        <span>
                            {fixCorruptedEncoding(source.name)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
      )}

      {}
      {showNpsSurvey && npsQuestion && (
        <div className={styles.npsSurveyContainer}>
          <h4>{npsQuestion}</h4>
          {npsError && <div className={styles.npsErrorMessage}>{npsError}</div>}
          <div className={styles.npsScoreButtons}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
              <button
                key={score}
                className={`${styles.npsScoreButton} ${selectedNpsScore === score ? styles.selected : ''}`}
                onClick={() => handleNpsScoreSelect(score)}
                disabled={isSubmittingNps}
              >
                {score}
              </button>
            ))}
          </div>
           {isSubmittingNps && <div className={styles.npsLoading}>Guardando...</div>}
           <button
             className={styles.npsCloseButton}
             onClick={() => setShowNpsSurvey(false)}
             disabled={isSubmittingNps}
             aria-label="Cerrar encuesta"
           >
             &times;
           </button>
        </div>
      )}

    </> 
  );
}


export function useNovaChat(options) {
  useEffect(() => {
  }, [options]);
  return null;
}

export function init(options) {  
  return options;
}
